/**
 * The host half of the Autoruns panel: the project's hooks and autoruns, their
 * last results, and what is running right now.
 *
 * Served by molecule-dev's API (`api/src/ai/hooks-handlers.ts`) over the same
 * sandbox files the `/hooks` command and Synthase's `manage_hooks` tool edit
 * (`.synthase/hooks.json` + `.agents/settings.json`), so the three surfaces
 * always agree.
 *
 * @module
 */

/** The events a hook can run on. */
export type HookEvent =
  'PreToolUse' | 'PostToolUse' | 'PostDeploy' | 'TaskEnd' | 'Stop' | 'FileChanged'

/** The events the platform runs when something happens (not around a tool call). */
export const AUTORUN_EVENTS: readonly HookEvent[] = ['PostDeploy', 'TaskEnd', 'Stop', 'FileChanged']

/** What a hook does. */
export type HookType = 'command' | 'check' | 'agent'

/** What Synthase is told about a run. */
export type HookFeedback = 'on-failure' | 'always' | 'never'

/** One user hook, as the API lists it. */
export interface ListedHook {
  id: number
  source: 'synthase' | 'settings'
  event: HookEvent
  name?: string
  matcher: string
  type: HookType
  command: string
  steps?: string[]
  e2e?: string
  prompt?: string
  enabled: boolean
  feedback?: HookFeedback
  timeout?: number
}

/** One built-in hook. */
export interface ListedBuiltIn {
  name: string
  event: HookEvent
  matcher: string
  type: HookType
  steps?: string[]
  feedback?: HookFeedback
  enabled: boolean
  canDisable: boolean
  overriddenBy?: number
}

/** One recorded run. */
export interface HookRun {
  name: string
  event: HookEvent
  type: HookType
  status: 'passed' | 'failed' | 'started' | 'error'
  exitCode?: number
  at: string
  durationMs: number
  subject?: string
  output: string
  fedBack: boolean
  builtIn?: boolean
}

/** `GET /projects/:id/hooks`. */
export interface HooksState {
  sandbox: 'running' | 'unavailable'
  hooks: ListedHook[]
  builtIns: ListedBuiltIn[]
  lastRuns: Record<string, HookRun>
  recentRuns: HookRun[]
  invalidFiles: string[]
  checkSteps: string[]
}

/** One background command or agent of a live turn. */
export interface RunningItem {
  id: string
  kind: 'command' | 'agent'
  conversationId: string
  label: string
  status: 'running' | 'succeeded' | 'failed'
  startedAt: string
  finishedAt?: string
  exitCode?: number
}

/** One session (conversation) of the project. */
export interface SessionSummary {
  conversationId: string
  title: string
  working: string | null
  active: boolean
  idleMs: number
}

/** `GET /projects/:id/automation`. */
export interface AutomationState {
  running: RunningItem[]
  sessions: SessionSummary[]
}

/** One scheduled task (`GET /projects/:id/scheduled-tasks`). */
export interface ScheduledTaskRow {
  id: string
  prompt: string
  mode: 'scheduled' | 'idle'
  runAt: string | null
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  lastError?: string | null
}

/** The fields a new or edited hook carries. */
export interface HookDraft {
  event: HookEvent
  matcher: string
  type: HookType
  command: string
  steps: string[]
  prompt: string
  name: string
  feedback: HookFeedback
}

/** The minimum of the bonded HTTP client this module needs. */
export interface HooksHttpClient {
  get<T>(url: string): Promise<{ data: T }>
  post<T>(url: string, body?: unknown): Promise<{ data: T }>
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>
  delete<T>(url: string): Promise<{ data: T }>
}

const base = (projectId: string): string => `/projects/${encodeURIComponent(projectId)}`

/** A hook's address in the routes: its name when it has one, else its number. */
export function hookRef(hook: { name?: string; id?: number }): string {
  return encodeURIComponent(hook.name ?? String(hook.id ?? ''))
}

/**
 * Load the hooks panel state.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @returns Hooks, built-ins and last runs.
 */
export async function loadHooks(http: HooksHttpClient, projectId: string): Promise<HooksState> {
  const { data } = await http.get<HooksState>(`${base(projectId)}/hooks`)
  return {
    sandbox: data?.sandbox ?? 'unavailable',
    hooks: data?.hooks ?? [],
    builtIns: data?.builtIns ?? [],
    lastRuns: data?.lastRuns ?? {},
    recentRuns: data?.recentRuns ?? [],
    invalidFiles: data?.invalidFiles ?? [],
    checkSteps: data?.checkSteps ?? ['typecheck', 'lint', 'build', 'unit', 'e2e'],
  }
}

/**
 * Load what is running right now and the scheduled tasks.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @returns Live work, sessions and scheduled tasks.
 */
export async function loadAutomation(
  http: HooksHttpClient,
  projectId: string,
): Promise<AutomationState & { scheduled: ScheduledTaskRow[] }> {
  const [live, scheduled] = await Promise.all([
    http.get<AutomationState>(`${base(projectId)}/automation`),
    http
      .get<{ tasks?: ScheduledTaskRow[] } | ScheduledTaskRow[]>(
        `${base(projectId)}/scheduled-tasks`,
      )
      .catch(() => ({ data: [] as ScheduledTaskRow[] })),
  ])
  const rows = Array.isArray(scheduled.data) ? scheduled.data : (scheduled.data?.tasks ?? [])
  return {
    running: live.data?.running ?? [],
    sessions: live.data?.sessions ?? [],
    scheduled: rows,
  }
}

/** The request body for a draft (only the fields its type uses). */
export function draftBody(draft: HookDraft): Record<string, unknown> {
  return {
    event: draft.event,
    ...(draft.matcher.trim() ? { matcher: draft.matcher.trim() } : {}),
    type: draft.type,
    ...(draft.type === 'command' ? { command: draft.command } : {}),
    ...(draft.type === 'check' && draft.steps.length ? { steps: draft.steps } : {}),
    ...(draft.type === 'agent' ? { prompt: draft.prompt } : {}),
    ...(draft.name.trim() ? { name: draft.name.trim() } : {}),
    feedback: draft.feedback,
  }
}

/** A blank draft. */
export function emptyDraft(): HookDraft {
  return {
    event: 'Stop',
    matcher: '',
    type: 'check',
    command: '',
    steps: ['typecheck', 'lint'],
    prompt: '',
    name: '',
    feedback: 'on-failure',
  }
}

/** A draft from an existing hook, for editing. */
export function draftFrom(hook: ListedHook): HookDraft {
  return {
    event: hook.event,
    matcher: hook.matcher,
    type: hook.type,
    command: hook.command,
    steps: hook.steps ?? [],
    prompt: hook.prompt ?? '',
    name: hook.name ?? '',
    feedback: hook.feedback ?? 'on-failure',
  }
}

/** Why a draft cannot be saved yet, or `null` when it can. */
export function draftProblem(draft: HookDraft): 'command' | 'prompt' | 'steps' | null {
  if (draft.type === 'command' && !draft.command.trim()) return 'command'
  if (draft.type === 'agent' && draft.prompt.trim().length < 10) return 'prompt'
  if (draft.type === 'check' && draft.steps.length === 0) return 'steps'
  if (!AUTORUN_EVENTS.includes(draft.event) && draft.type !== 'command') return 'command'
  return null
}

/**
 * Add a hook.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param draft - The hook.
 */
export async function addHook(
  http: HooksHttpClient,
  projectId: string,
  draft: HookDraft,
): Promise<void> {
  await http.post(`${base(projectId)}/hooks`, draftBody(draft))
}

/**
 * Save an edited hook.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param ref - The hook's ref ({@link hookRef}).
 * @param draft - Its new fields.
 */
export async function updateHook(
  http: HooksHttpClient,
  projectId: string,
  ref: string,
  draft: HookDraft,
): Promise<void> {
  const { event: _event, ...body } = draftBody(draft)
  await http.patch(`${base(projectId)}/hooks/${ref}`, body)
}

/**
 * Switch a hook (or a built-in autorun) on or off.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param ref - The hook's ref.
 * @param enabled - On or off.
 */
export async function setHookEnabled(
  http: HooksHttpClient,
  projectId: string,
  ref: string,
  enabled: boolean,
): Promise<void> {
  await http.patch(`${base(projectId)}/hooks/${ref}`, { enabled })
}

/**
 * Remove a hook.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param ref - The hook's ref.
 */
export async function removeHook(
  http: HooksHttpClient,
  projectId: string,
  ref: string,
): Promise<void> {
  await http.delete(`${base(projectId)}/hooks/${ref}`)
}

/**
 * Run an autorun now.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param ref - The hook's ref.
 * @returns Its run.
 */
export async function runHookNow(
  http: HooksHttpClient,
  projectId: string,
  ref: string,
): Promise<HookRun> {
  const { data } = await http.post<{ run: HookRun }>(`${base(projectId)}/hooks/${ref}/run`)
  return data.run
}

/**
 * Cancel a scheduled task.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @param taskId - The task.
 */
export async function cancelScheduledTask(
  http: HooksHttpClient,
  projectId: string,
  taskId: string,
): Promise<void> {
  await http.delete(`${base(projectId)}/scheduled-tasks/${encodeURIComponent(taskId)}`)
}

/** A short human duration: `850ms`, `34s`, `2m 5s`. */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}
